import { ColumnDirective, ColumnModel, ColumnsDirective, ValueType } from '@syncfusion/ej2-react-grids';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import { Report, ReportStatus } from '../../../model';
import { ReportTableRow } from '../../../utils/ReportUtils';

export const REPORT_NAME_CELL_FOCUS_CLASS = 'report-name-cell-focus';
export const CHECKBOX_COLUMN_WIDTH_PX = 40;
export const REPORT_NAME_COLUMN_WIDTH_PX = 420;
export const REPORT_NAME_COLUMN_FIELD = 'name';

export type ReportsTableColumnsMode = 'browse' | 'edit';

export interface IReportsTableTemplates {
    warningIcon: (item: Report) => ReactNode;
    reportName: (item: Report) => ReactNode;
    executed: (item: Report) => ReactNode;
    status: (item: Report) => ReactNode;
}

export interface IReportsTableColumnsProps {
    templates: IReportsTableTemplates;
    mode: ReportsTableColumnsMode;
    isColumnVisible: (field: string) => boolean;
    reportHeaderTemplate: (item: ColumnModel) => React.ReactNode;
    getColumnTemplate: (fieldName: string) => (item: ReportTableRow) => React.ReactNode;
    getBooleanColumnTemplate: (fieldName: string) => (item: ReportTableRow) => React.ReactNode;
    statusFilterItemTemplate: (data: { status?: ReportStatus }) => React.ReactNode;
    sortComparer: (reference: ValueType, comparer: ValueType) => number;
    t: TFunction;
}

/** Returns ColumnsDirective as a direct Grid child. Do not render this as <ReportsTableColumns /> — Syncfusion only inspects its own immediate children for column/inject nodes. */
export const renderReportsTableColumns = ({
    templates,
    mode,
    isColumnVisible,
    reportHeaderTemplate,
    getColumnTemplate,
    getBooleanColumnTemplate,
    statusFilterItemTemplate,
    sortComparer,
    t,
}: IReportsTableColumnsProps) => {
    const isBrowse = mode === 'browse';
    const reportNameWidth = isBrowse ? REPORT_NAME_COLUMN_WIDTH_PX : REPORT_NAME_COLUMN_WIDTH_PX + CHECKBOX_COLUMN_WIDTH_PX;

    return (
        <ColumnsDirective>
            <ColumnDirective field='id' isPrimaryKey={true} visible={false} showInColumnChooser={false} />
            {isBrowse ? (
                <ColumnDirective
                    type='checkbox'
                    template={templates.warningIcon}
                    width={CHECKBOX_COLUMN_WIDTH_PX}
                    minWidth={CHECKBOX_COLUMN_WIDTH_PX}
                    allowResizing={false}
                    showInColumnChooser={false}
                    isFrozen={true}
                />
            ) : null}
            <ColumnDirective
                field={REPORT_NAME_COLUMN_FIELD}
                customAttributes={{ class: REPORT_NAME_CELL_FOCUS_CLASS }}
                headerText={t('report').toString()}
                headerTemplate={reportHeaderTemplate}
                template={templates.reportName}
                width={reportNameWidth}
                minWidth={reportNameWidth}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
                showInColumnChooser={false}
                isFrozen={true}
            />
            <ColumnDirective
                field='description'
                visible={isColumnVisible('description')}
                headerText={t('description').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('description')}
                width={250}
                minWidth={250}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='status'
                visible={isColumnVisible('status')}
                headerText={t('status').toString()}
                headerTemplate={reportHeaderTemplate}
                template={templates.status}
                filter={{ itemTemplate: statusFilterItemTemplate }}
                width={170}
                minWidth={170}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowSearching={false}
                allowFiltering={true}
            />
            <ColumnDirective
                field='entityCode'
                visible={isColumnVisible('entityCode')}
                headerText={t('entityCode').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('entityCode')}
                width={125}
                minWidth={125}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='entityName'
                visible={isColumnVisible('entityName')}
                headerText={t('entity').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('entityName')}
                width={110}
                minWidth={110}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='jurisdiction'
                visible={isColumnVisible('jurisdiction')}
                headerText={t('jurisdiction').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('jurisdiction')}
                width={125}
                minWidth={125}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='period'
                visible={isColumnVisible('period')}
                headerText={t('period').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('period')}
                width={110}
                minWidth={110}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='case'
                visible={isColumnVisible('case')}
                headerText={t('case').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('case')}
                width={102}
                minWidth={102}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='executed'
                visible={isColumnVisible('executed')}
                headerText={t('executed').toString()}
                headerTemplate={reportHeaderTemplate}
                template={templates.executed}
                width={140}
                minWidth={140}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowSearching={false}
                allowFiltering={true}
            />
            <ColumnDirective
                field='userFullName'
                visible={isColumnVisible('userFullName')}
                headerText={t('executedBy').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('userFullName')}
                width={150}
                minWidth={150}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='customGroupName'
                visible={isColumnVisible('customGroupName')}
                headerText={t('customGroup').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('customGroupName')}
                width={150}
                minWidth={150}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='ignoreEntityGroup'
                visible={isColumnVisible('ignoreEntityGroup')}
                headerText={t('useGroupOnly').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getBooleanColumnTemplate('ignoreEntityGroup')}
                width={130}
                minWidth={130}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='includeAllBU'
                visible={isColumnVisible('includeAllBU')}
                headerText={t('includeAllBu').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getBooleanColumnTemplate('includeAllBU')}
                width={130}
                minWidth={130}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='customRangeName'
                visible={isColumnVisible('customRangeName')}
                headerText={t('customRange').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('customRangeName')}
                width={150}
                minWidth={150}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='postAmountOption'
                visible={isColumnVisible('postAmountOption')}
                headerText={t('postAmountOption').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('postAmountOption')}
                width={220}
                minWidth={220}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
            <ColumnDirective
                field='postAmountGroupName'
                visible={isColumnVisible('postAmountGroupName')}
                headerText={t('postAmountGroup').toString()}
                headerTemplate={reportHeaderTemplate}
                template={getColumnTemplate('postAmountGroupName')}
                width={150}
                minWidth={150}
                headerTextAlign='Left'
                sortComparer={sortComparer}
                allowFiltering={true}
            />
        </ColumnsDirective>
    );
};

export default renderReportsTableColumns;
